import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  createSchedule,
  deleteSchedule,
  listSchedules,
  pauseSchedule,
  resumeSchedule,
  type Schedule,
} from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Schedules tab — list user-owned scheduled crawls, create new ones, and
 * pause / resume / delete existing ones. The in-process scheduler in the
 * API picks rows up on its minute tick; this UI just CRUDs rows.
 *
 * Deliberately lean: one page with inline create form + table. No cron
 * visual builder for v0 — the textbox plus a small preset palette covers
 * the 90% case ("nightly", "hourly", "weekly Mondays"). Users who want
 * something exotic can type the expression.
 */
export function SchedulesPage(): JSX.Element {
  const { user, ready } = useAuth();
  const [schedules, setSchedules] = useState<Schedule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!ready) return;
    if (!user) return;
    let cancelled = false;
    listSchedules()
      .then((list) => {
        if (!cancelled) setSchedules(list);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [ready, user]);

  async function handleCreate(input: {
    name: string;
    targetUrl: string;
    cronExpression: string;
    framework: string;
    maxPages: number;
  }): Promise<void> {
    setError(null);
    setBusy("create");
    try {
      const row = await createSchedule({
        name: input.name,
        targetUrl: input.targetUrl,
        cronExpression: input.cronExpression,
        crawlOptions: {
          framework: input.framework,
          maxPages: input.maxPages,
        },
      });
      setSchedules((prev) => [row, ...(prev ?? [])]);
      setShowCreate(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function handleToggle(row: Schedule): Promise<void> {
    setError(null);
    setBusy(row.id);
    try {
      const updated = row.enabled
        ? await pauseSchedule(row.id)
        : await resumeSchedule(row.id);
      setSchedules((prev) =>
        prev ? prev.map((s) => (s.id === updated.id ? updated : s)) : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete(row: Schedule): Promise<void> {
    if (!confirm(`Delete schedule "${row.name}"? This can't be undone.`)) return;
    setError(null);
    setBusy(row.id);
    try {
      await deleteSchedule(row.id);
      setSchedules((prev) => (prev ? prev.filter((s) => s.id !== row.id) : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  if (ready && !user) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-900 p-6 text-sm text-slate-300">
        <p className="font-medium text-slate-100">Sign in to manage schedules</p>
        <p className="mt-1 text-slate-400">
          Scheduled crawls are user-owned, so we need to know who you are before we
          can list or create them.
        </p>
        <Link
          to="/sign-in?redirect=/schedules"
          className="mt-4 inline-block rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400"
        >
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Schedules</h1>
          <p className="mt-1 text-sm text-slate-400">
            Cron-driven crawls. The API ticks every minute and picks up any due
            row — you don&apos;t need to leave the dashboard open.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500 font-mono">api: /api/schedules</div>
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400"
          >
            {showCreate ? "Cancel" : "+ New schedule"}
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-700 bg-rose-900/40 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {showCreate && (
        <CreateScheduleForm
          busy={busy === "create"}
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {schedules === null && !error && (
        <div className="rounded-lg border border-slate-800 bg-slate-900 p-6 text-sm text-slate-400">
          Loading…
        </div>
      )}

      {schedules !== null && schedules.length === 0 && !showCreate && (
        <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center">
          <p className="text-slate-300">No schedules yet.</p>
          <p className="mt-1 text-sm text-slate-500">
            Create one to crawl a target on a cron. Useful for nightly smoke runs
            or periodic selector health checks.
          </p>
        </div>
      )}

      {schedules && schedules.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3">Cron</th>
                <th className="px-4 py-3">Next run</th>
                <th className="px-4 py-3">Last run</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-950">
              {schedules.map((row) => (
                <ScheduleRow
                  key={row.id}
                  row={row}
                  busy={busy === row.id}
                  onToggle={() => handleToggle(row)}
                  onDelete={() => handleDelete(row)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ScheduleRow(props: {
  row: Schedule;
  busy: boolean;
  onToggle: () => void;
  onDelete: () => void;
}): JSX.Element {
  const { row, busy } = props;
  return (
    <tr className="text-slate-200">
      <td className="px-4 py-3 font-medium">{row.name}</td>
      <td className="px-4 py-3 font-mono text-xs text-slate-400">
        <a href={row.targetUrl} target="_blank" rel="noreferrer" className="hover:text-slate-200">
          {shortenUrl(row.targetUrl)}
        </a>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-400">{row.cronExpression}</td>
      <td className="px-4 py-3 text-slate-400">{formatTs(row.nextRunAt, "—")}</td>
      <td className="px-4 py-3 text-slate-400">{formatTs(row.lastRunAt, "never")}</td>
      <td className="px-4 py-3">
        {row.enabled ? (
          <span className="rounded-full bg-emerald-900/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-300">
            enabled
          </span>
        ) : (
          <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-400">
            paused
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          onClick={props.onToggle}
          disabled={busy}
          className="mr-2 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-300 hover:border-slate-600 disabled:opacity-50"
        >
          {row.enabled ? "Pause" : "Resume"}
        </button>
        <button
          type="button"
          onClick={props.onDelete}
          disabled={busy}
          className="rounded-md border border-rose-900 bg-rose-950/40 px-2 py-1 text-xs text-rose-300 hover:border-rose-800 disabled:opacity-50"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}

// ─────────────────────── Create form ───────────────────────

const CRON_PRESETS: { label: string; expr: string }[] = [
  { label: "Every hour", expr: "0 * * * *" },
  { label: "Nightly at 02:00 UTC", expr: "0 2 * * *" },
  { label: "Every Monday 09:00 UTC", expr: "0 9 * * 1" },
  { label: "Every 15 minutes", expr: "*/15 * * * *" },
];

function CreateScheduleForm(props: {
  busy: boolean;
  onSubmit: (input: {
    name: string;
    targetUrl: string;
    cronExpression: string;
    framework: string;
    maxPages: number;
  }) => void;
  onCancel: () => void;
}): JSX.Element {
  const [name, setName] = useState("Nightly smoke");
  const [targetUrl, setTargetUrl] = useState("https://www.saucedemo.com/");
  const [cronExpression, setCronExpression] = useState("0 2 * * *");
  const [framework, setFramework] = useState("playwright-ts");
  const [maxPages, setMaxPages] = useState(5);

  function submit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    props.onSubmit({ name, targetUrl, cronExpression, framework, maxPages });
  }

  return (
    <form
      onSubmit={submit}
      className="mb-6 rounded-lg border border-slate-800 bg-slate-900/60 p-4"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="block text-sm">
          <span className="mb-1 block text-slate-400">Name</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-slate-400">Target URL</span>
          <input
            required
            type="url"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-slate-100"
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-slate-400">
            Cron expression{" "}
            <span className="text-xs text-slate-500">(m h dom mon dow, UTC)</span>
          </span>
          <input
            required
            value={cronExpression}
            onChange={(e) => setCronExpression(e.target.value)}
            className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-slate-100"
          />
          <div className="mt-2 flex flex-wrap gap-1">
            {CRON_PRESETS.map((p) => (
              <button
                key={p.expr}
                type="button"
                onClick={() => setCronExpression(p.expr)}
                className="rounded border border-slate-800 bg-slate-900 px-2 py-0.5 text-[11px] text-slate-400 hover:border-slate-600 hover:text-slate-200"
              >
                {p.label}
              </button>
            ))}
          </div>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="mb-1 block text-slate-400">Framework</span>
            <select
              value={framework}
              onChange={(e) => setFramework(e.target.value)}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
            >
              <option value="selenium-java">Selenium · Java</option>
              <option value="selenium-python">Selenium · Python</option>
              <option value="playwright-ts">Playwright · TypeScript</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-400">Max pages</span>
            <input
              type="number"
              min={1}
              max={50}
              value={maxPages}
              onChange={(e) => setMaxPages(Number(e.target.value))}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
            />
          </label>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={props.onCancel}
          className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-300 hover:border-slate-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={props.busy}
          className="rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50"
        >
          {props.busy ? "Creating…" : "Create schedule"}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────── Helpers ───────────────────────

function shortenUrl(url: string): string {
  try {
    const u = new URL(url);
    const tail = u.pathname === "/" ? "" : u.pathname;
    return u.host + tail;
  } catch {
    return url;
  }
}

function formatTs(ms: number | null, fallback: string): string {
  if (ms === null) return fallback;
  const d = new Date(ms);
  const now = Date.now();
  const delta = ms - now;
  const abs = Math.abs(delta);
  const mins = Math.round(abs / 60_000);
  if (abs < 60_000) return "just now";
  if (mins < 60) return `${delta > 0 ? "in " : ""}${mins}m${delta < 0 ? " ago" : ""}`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${delta > 0 ? "in " : ""}${hours}h${delta < 0 ? " ago" : ""}`;
  return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
}
